// Which role takes which link when work is sent to ONE machine (#6767, epic #6552). Pure,
// exhaustive over the nine stored mode keys, so a tenth key is a type error until it is
// given a row. Read by the route (`apps/api` `POST /v1/runners/:id/work`, and `box send`)
// and by the Fleet card's Send work form (#6770).
//
// Owner ruling (ivndev001, 2026-10-30, attended, #6767 issuecomment-6514820561): Reviewer
// takes pull requests; Coder, Scout and Escalation take issues only; CI Fixer, Triage and
// Planner are refused (`role_not_on_machines`); DevOps and Gardener take neither. CI
// Fixer's api-reachable babysit mint is a follow-up, after which its `machineLane` turns
// true.

import type { AgentModeKey } from '../entities/agent-mode';
import type { WorkLinkKind } from './work-link';

/**
 * How long a sent task waits for its machine before it returns to the open queue.
 * Owner ruling (ivndev001, 2026-10-20, collab planning): "Send work uses a hard target on
 * the task; if unclaimed after 10 min it returns to the open queue." No env knob.
 */
export const TARGET_RELEASE_MS = 600_000;

export type SendRole = {
  /** The link kinds this role takes; empty = takes neither. */
  readonly takes: readonly WorkLinkKind[];
  /** Whether a send to a machine can mint this role's work in this slice. */
  readonly machineLane: boolean;
};

export const SEND_ROLES: Readonly<Record<AgentModeKey, SendRole>> = {
  worker: { takes: ['issue'], machineLane: true },
  reviewer: { takes: ['pr'], machineLane: true },
  scout: { takes: ['issue'], machineLane: true },
  escalation: { takes: ['issue'], machineLane: true },
  ci_fixer: { takes: ['pr'], machineLane: false },
  planner: { takes: ['issue'], machineLane: false },
  triage: { takes: ['issue'], machineLane: false },
  devops: { takes: [], machineLane: false },
  gardener: { takes: [], machineLane: false },
};

/** The table's refusal for one (role, link kind), checked IN ORDER; null = the role takes it. */
export type SendRoleRefusal = 'role_wrong_kind' | 'role_not_on_machines';

/**
 * The table applied in its one order, before any read: `takes` empty is the wrong kind;
 * no machine lane is `role_not_on_machines` (so CI Fixer with a PR link, and Planner or
 * Triage with an issue link, are never "the wrong kind"); then the link's kind.
 */
export function sendRoleVerdict(role: AgentModeKey, kind: WorkLinkKind): SendRoleRefusal | null {
  const row = SEND_ROLES[role];
  if (row.takes.length === 0) return 'role_wrong_kind';
  if (!row.machineLane) return 'role_not_on_machines';
  return row.takes.includes(kind) ? null : 'role_wrong_kind';
}
